import { useEffect, useMemo, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { formatLocationLabel } from '../../../shared/lib/locations';
import { useCategorias, useCostCenters, useFornecedores, useLocations, useProjects, useClients } from '../hooks';
import type { AssetCategoryGroup, AssetDetail, CreateAssetPayload } from '../api';

const GROUP_LABELS: Record<AssetCategoryGroup, string> = {
  it_equipment: 'Equipamentos de TI',
  mobile_device: 'Dispositivos móveis',
  furniture: 'Mobiliário e equipamentos de apoio',
  software: 'Software',
};

const FURNITURE_SPEC_FIELDS = [
  { key: 'material', label: 'Material' },
  { key: 'color', label: 'Cor' },
  { key: 'structure', label: 'Estrutura' },
  { key: 'upholstery', label: 'Revestimento' },
  { key: 'backrest', label: 'Encosto' },
  { key: 'wheels', label: 'Rodízios' },
  { key: 'supportedWeight', label: 'Peso suportado' },
  { key: 'dimensions', label: 'Dimensões (L x A x P)' },
] as const;

type FurnitureSpecKey = (typeof FURNITURE_SPEC_FIELDS)[number]['key'];

/**
 * O backend armazena `specs` como jsonb; a UI mostra texto plano ao
 * usuário final. Convenção: `specs.description` guarda o texto livre.
 * Ativos legados podem ter outras chaves — nesse caso caímos num
 * `JSON.stringify` bonito só pra não perder informação na tela.
 */
function specsToText(
  specs: Record<string, unknown> | null | undefined,
  furniture = false,
): string {
  if (!specs || Object.keys(specs).length === 0) return '';
  if (furniture) return typeof specs.description === 'string' ? specs.description : '';
  if (Object.keys(specs).length === 1 && typeof specs.description === 'string') {
    return specs.description;
  }
  return JSON.stringify(specs, null, 2);
}

interface Props {
  initial?: AssetDetail;
  onSubmit: (payload: CreateAssetPayload) => Promise<void> | void;
  onCancel: () => void;
  submitting?: boolean;
  submitLabel?: string;
}

type FormState = {
  assetCode: string;
  serialNumber: string;
  barcode: string;
  description: string;
  categoryId: string;
  brand: string;
  model: string;
  supplierId: string;
  purchaseDate: string;
  purchaseValue: string;
  invoiceNumber: string;
  costCenterId: string;
  projectId: string;
  clientId: string;
  warrantyMonths: string;
  warrantyExpiresAt: string;
  locationId: string;
  locationBuilding: string;
  locationFloor: string;
  locationRoom: string;
  locationAisle: string;
  locationShelf: string;
  locationPosition: string;
  notes: string;
  specsText: string;
  furnitureSpecs: Record<FurnitureSpecKey, string>;
  companyId: string;
};

function emptyFurnitureSpecs(): Record<FurnitureSpecKey, string> {
  return {
    material: '',
    color: '',
    structure: '',
    upholstery: '',
    backrest: '',
    wheels: '',
    supportedWeight: '',
    dimensions: '',
  };
}

function furnitureSpecsFrom(specs: Record<string, unknown>): Record<FurnitureSpecKey, string> {
  return {
    material: specs.material == null ? '' : String(specs.material),
    color: specs.color == null ? '' : String(specs.color),
    structure: specs.structure == null ? '' : String(specs.structure),
    upholstery: specs.upholstery == null ? '' : String(specs.upholstery),
    backrest: specs.backrest == null ? '' : String(specs.backrest),
    wheels: specs.wheels == null ? '' : String(specs.wheels),
    supportedWeight: specs.supportedWeight == null ? '' : String(specs.supportedWeight),
    dimensions: specs.dimensions == null ? '' : String(specs.dimensions),
  };
}

function toInitialState(initial?: AssetDetail): FormState {
  const initialSpecs = initial?.asset.specs ?? {};
  if (!initial) {
    return {
      assetCode: '', serialNumber: '', description: '', categoryId: '',
      barcode: '',
      brand: '', model: '', supplierId: '', purchaseDate: '',
      purchaseValue: '', invoiceNumber: '', costCenterId: '', projectId: '',
      clientId: '', warrantyMonths: '', warrantyExpiresAt: '', locationId: '',
      locationBuilding: '', locationFloor: '', locationRoom: '', locationAisle: '',
      locationShelf: '', locationPosition: '',
      notes: '', specsText: '', furnitureSpecs: emptyFurnitureSpecs(), companyId: '',
    };
  }
  return {
    assetCode: initial.asset.assetCode,
    serialNumber: initial.asset.serialNumber ?? '',
    barcode: initial.asset.barcode ?? '',
    description: initial.asset.description,
    categoryId: initial.category?.id ?? '',
    brand: initial.asset.brand ?? '',
    model: initial.asset.model ?? '',
    supplierId: initial.supplier?.id ?? '',
    purchaseDate: initial.asset.purchaseDate ?? '',
    purchaseValue: initial.asset.purchaseValue ?? '',
    invoiceNumber: initial.asset.invoiceNumber ?? '',
    costCenterId: initial.costCenter?.id ?? '',
    projectId: initial.project?.id ?? '',
    clientId: initial.client?.id ?? '',
    warrantyMonths: initial.asset.warrantyMonths?.toString() ?? '',
    warrantyExpiresAt: initial.asset.warrantyExpiresAt ?? '',
    locationId: initial.location?.id ?? '',
    locationBuilding: initial.detailedLocation.building ?? '',
    locationFloor: initial.detailedLocation.floor ?? '',
    locationRoom: initial.detailedLocation.room ?? '',
    locationAisle: initial.detailedLocation.aisle ?? '',
    locationShelf: initial.detailedLocation.shelf ?? '',
    locationPosition: initial.detailedLocation.position ?? '',
    notes: initial.asset.notes ?? '',
    specsText: specsToText(initialSpecs, initial.category?.group === 'furniture'),
    furnitureSpecs: furnitureSpecsFrom(initialSpecs),
    companyId: initial.company?.id ?? '',
  };
}

// Este formulário é exclusivo para ATIVOS FÍSICOS. Licenças de software
// (que compartilham o backend `assets` só no assetType, e vivem
// principalmente na tabela `licenses` com seats) têm seu próprio form
// em LicencaForm — acessível pelo dropdown "Novo → Software/Licença".
// Aqui as categorias exibidas são apenas dos grupos físicos.
const PHYSICAL_GROUPS: AssetCategoryGroup[] = ['it_equipment', 'mobile_device', 'furniture'];

export function AtivoForm({ initial, onSubmit, onCancel, submitting, submitLabel = 'Salvar' }: Props) {
  const [form, setForm] = useState<FormState>(() => toInitialState(initial));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Dirty flag do vencimento: para de auto-calcular quando o usuário
  // editou o campo à mão. Botão "recalcular" reseta.
  const [warrantyDirty, setWarrantyDirty] = useState<boolean>(
    () => Boolean(initial?.asset.warrantyExpiresAt),
  );

  const categoriasQuery = useCategorias();
  const locationsQuery = useLocations();
  const costCentersQuery = useCostCenters();
  const projectsQuery = useProjects();
  const clientsQuery = useClients();
  const fornecedoresQuery = useFornecedores();

  useEffect(() => {
    setForm(toInitialState(initial));
    setWarrantyDirty(Boolean(initial?.asset.warrantyExpiresAt));
  }, [initial]);

  // Grupos físicos apenas. Categorias de software não aparecem aqui —
  // o usuário cria licença pelo LicencaForm (dropdown "Novo").
  const visibleGroups = (categoriasQuery.data?.groups ?? []).filter((g) =>
    PHYSICAL_GROUPS.includes(g.group),
  );

  // Achata os `<optgroup>` do select nativo em uma lista plana com
  // `description` = nome do grupo. O AutocompleteSelect não suporta
  // agrupamento mas mostra a descrição abaixo do label na dropdown.
  const categoriaOptions = useMemo(
    () =>
      visibleGroups.flatMap((g) =>
        g.items.map((i) => ({
          value: i.id,
          label: i.name,
          description: GROUP_LABELS[g.group] ?? g.group,
        })),
      ),
    [visibleGroups],
  );
  const selectedCategoryGroup = visibleGroups.find((group) =>
    group.items.some((item) => item.id === form.categoryId),
  )?.group;

  const fornecedorOptions = useMemo(
    () =>
      (fornecedoresQuery.data?.data ?? []).map((s) => ({
        value: s.id,
        label: s.tradeName ?? s.corporateName,
      })),
    [fornecedoresQuery.data],
  );
  const costCenterOptions = useMemo(
    () => (costCentersQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [costCentersQuery.data],
  );
  const projectOptions = useMemo(
    () => (projectsQuery.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    [projectsQuery.data],
  );
  const clientOptions = useMemo(
    () => (clientsQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clientsQuery.data],
  );
  const locationOptions = useMemo(() => {
    const options = (locationsQuery.data?.data ?? []).map((l) => ({
      value: l.id,
      label: l.name,
      description: formatLocationLabel({ name: l.name, type: l.type, clientName: l.clientName })
        .replace(`${l.name} · `, ''),
    }));
    // A lista só traz localidades ativas. Se a do ativo foi inativada depois
    // da alocação, ela precisa continuar selecionável — senão o campo abre em
    // branco e um salvamento qualquer registra "Localização removida".
    const current = initial?.location;
    if (current && !options.some((o) => o.value === current.id)) {
      options.push({
        value: current.id,
        label: current.name,
        description: current.active === false ? 'Localização inativa' : 'Localização atual',
      });
    }
    return options;
  }, [locationsQuery.data, initial?.location]);

  // Auto-cálculo do vencimento: só roda enquanto o usuário não editou
  // o campo manualmente. purchaseDate + warrantyMonths → warrantyExpiresAt.
  useEffect(() => {
    if (warrantyDirty) return;
    if (!form.purchaseDate || !form.warrantyMonths) return;
    const months = Number(form.warrantyMonths);
    if (!Number.isFinite(months) || months <= 0) return;
    const base = new Date(form.purchaseDate + 'T00:00:00');
    if (Number.isNaN(base.getTime())) return;
    const expires = new Date(base);
    expires.setMonth(expires.getMonth() + months);
    const iso = expires.toISOString().slice(0, 10);
    if (iso !== form.warrantyExpiresAt) {
      setForm((prev) => ({ ...prev, warrantyExpiresAt: iso }));
    }
  }, [form.purchaseDate, form.warrantyMonths, warrantyDirty, form.warrantyExpiresAt]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function validate(): boolean {
    const next: Record<string, string> = {};
    // Em edição, `assetCode` não vai no payload (imutável); não validamos.
    if (!initial && !form.assetCode.trim()) next.assetCode = 'Código patrimonial é obrigatório';
    if (!form.description.trim()) next.description = 'Descrição é obrigatória';
    if (!form.categoryId) next.categoryId = 'Categoria é obrigatória';
    if (form.purchaseDate) {
      const d = new Date(form.purchaseDate);
      if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now()) {
        next.purchaseDate = 'Data da compra não pode ser futura';
      }
    }
    if (form.purchaseValue && Number(form.purchaseValue) < 0) {
      next.purchaseValue = 'Valor de aquisição não pode ser negativo';
    }
    if (form.warrantyMonths && Number(form.warrantyMonths) < 0) {
      next.warrantyMonths = 'Garantia (meses) não pode ser negativa';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const isEdit = Boolean(initial);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    // Em edição, `assetCode` é IMUTÁVEL (RN001 + trilha de auditoria). O
    // UpdateAssetDto rejeita a propriedade com 400 "property assetCode
    // should not exist" — o whitelist é intencional. Só enviamos o
    // código na criação.
    const payload: CreateAssetPayload = {
      description: form.description.trim(),
      categoryId: form.categoryId,
      ...(isEdit ? {} : { assetCode: form.assetCode.trim() }),
    } as CreateAssetPayload;
    if (form.serialNumber) payload.serialNumber = form.serialNumber.trim();
    if (form.barcode.trim() || isEdit) payload.barcode = form.barcode.trim();
    if (form.brand) payload.brand = form.brand.trim();
    if (form.model) payload.model = form.model.trim();
    if (form.supplierId) payload.supplierId = form.supplierId;
    if (form.purchaseDate) payload.purchaseDate = form.purchaseDate;
    if (form.purchaseValue) payload.purchaseValue = Number(form.purchaseValue);
    if (form.invoiceNumber) payload.invoiceNumber = form.invoiceNumber.trim();
    if (form.costCenterId) payload.costCenterId = form.costCenterId;
    if (form.projectId) payload.projectId = form.projectId;
    if (form.clientId) payload.clientId = form.clientId;
    if (form.warrantyMonths) payload.warrantyMonths = Number(form.warrantyMonths);
    if (form.warrantyExpiresAt) payload.warrantyExpiresAt = form.warrantyExpiresAt;
    if (form.locationId) payload.locationId = form.locationId;
    if (form.locationBuilding.trim() || isEdit) payload.locationBuilding = form.locationBuilding.trim();
    if (form.locationFloor.trim() || isEdit) payload.locationFloor = form.locationFloor.trim();
    if (form.locationRoom.trim() || isEdit) payload.locationRoom = form.locationRoom.trim();
    if (form.locationAisle.trim() || isEdit) payload.locationAisle = form.locationAisle.trim();
    if (form.locationShelf.trim() || isEdit) payload.locationShelf = form.locationShelf.trim();
    if (form.locationPosition.trim() || isEdit) payload.locationPosition = form.locationPosition.trim();
    if (form.notes) payload.notes = form.notes;
    if (selectedCategoryGroup === 'furniture') {
      const specs = { ...(initial?.asset.specs ?? {}) };
      for (const { key } of FURNITURE_SPEC_FIELDS) {
        delete specs[key];
        const value = form.furnitureSpecs[key].trim();
        if (value) specs[key] = value;
      }
      if (form.specsText.trim()) specs.description = form.specsText.trim();
      else delete specs.description;
      payload.specs = specs;
    } else if (form.specsText.trim()) {
      payload.specs = { description: form.specsText.trim() };
    }
    if (form.companyId) payload.companyId = form.companyId;

    await onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Identificação</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput
            label={`Código patrimonial${!isEdit ? ' *' : ''}`}
            value={form.assetCode}
            onChange={(e) => set('assetCode', e.target.value)}
            disabled={isEdit}
            error={errors.assetCode}
            title={isEdit ? 'Código patrimonial é imutável após emissão (auditoria).' : undefined}
          />
          <TextInput
            label="Número de série"
            value={form.serialNumber}
            onChange={(e) => set('serialNumber', e.target.value)}
          />
          <TextInput
            label="Código de barras"
            value={form.barcode}
            onChange={(e) => set('barcode', e.target.value)}
          />
          <div className="md:col-span-2">
            <TextInput
              label="Descrição *"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              error={errors.description}
            />
          </div>
          <AutocompleteSelect
            label="Categoria *"
            options={categoriaOptions}
            value={form.categoryId}
            onChange={(v) => set('categoryId', v)}
            error={errors.categoryId}
          />
          <TextInput
            label="Marca"
            value={form.brand}
            onChange={(e) => set('brand', e.target.value)}
          />
          <TextInput
            label="Modelo"
            value={form.model}
            onChange={(e) => set('model', e.target.value)}
          />
        </div>
        {isEdit && (
          <p className="mt-2 text-xs text-gray-500">
            Código patrimonial é imutável após emissão — protege a rastreabilidade com etiquetas/QR físicos.
          </p>
        )}
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Aquisição</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <AutocompleteSelect
            label="Fornecedor"
            options={fornecedorOptions}
            value={form.supplierId}
            onChange={(v) => set('supplierId', v)}
          />
          <TextInput
            label="Data da compra"
            type="date"
            value={form.purchaseDate}
            onChange={(e) => set('purchaseDate', e.target.value)}
            error={errors.purchaseDate}
          />
          <TextInput
            label="Valor de aquisição (R$)"
            type="number"
            step="0.01"
            value={form.purchaseValue}
            onChange={(e) => set('purchaseValue', e.target.value)}
            error={errors.purchaseValue}
          />
          <TextInput
            label="Nota fiscal"
            value={form.invoiceNumber}
            onChange={(e) => set('invoiceNumber', e.target.value)}
          />
          <AutocompleteSelect
            label="Centro de custo"
            options={costCenterOptions}
            value={form.costCenterId}
            onChange={(v) => set('costCenterId', v)}
          />
          <AutocompleteSelect
            label="Projeto"
            options={projectOptions}
            value={form.projectId}
            onChange={(v) => set('projectId', v)}
          />
          <AutocompleteSelect
            label="Cliente"
            options={clientOptions}
            value={form.clientId}
            onChange={(v) => set('clientId', v)}
          />
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Garantia</h3>
          {warrantyDirty && form.purchaseDate && form.warrantyMonths && (
            <button
              type="button"
              onClick={() => setWarrantyDirty(false)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
              title="Voltar ao valor calculado a partir da data da compra e meses de garantia"
            >
              <RefreshCcw className="h-3 w-3" /> Recalcular vencimento
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput
            label="Garantia (meses)"
            type="number"
            min="0"
            value={form.warrantyMonths}
            onChange={(e) => set('warrantyMonths', e.target.value)}
            error={errors.warrantyMonths}
          />
          <TextInput
            label={
              warrantyDirty
                ? 'Vencimento da garantia (editado manualmente)'
                : 'Vencimento da garantia (auto-calculado)'
            }
            type="date"
            value={form.warrantyExpiresAt}
            onChange={(e) => {
              setWarrantyDirty(true);
              set('warrantyExpiresAt', e.target.value);
            }}
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Localização do ativo</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <AutocompleteSelect
              label="Localização do Ativo (local de alocação)"
              options={locationOptions}
              value={form.locationId}
              onChange={(v) => set('locationId', v)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Onde o ativo está alocado (unidade, cliente, estoque, assistência
              técnica…). É diferente do responsável pela guarda do equipamento.
              Só localizações ativas aparecem aqui — exceto a atual do ativo,
              quando ela foi inativada depois da alocação.
            </p>
          </div>
          <TextInput
            label="Prédio"
            value={form.locationBuilding}
            onChange={(e) => set('locationBuilding', e.target.value)}
          />
          <TextInput
            label="Andar"
            value={form.locationFloor}
            onChange={(e) => set('locationFloor', e.target.value)}
          />
          <TextInput
            label="Sala"
            value={form.locationRoom}
            onChange={(e) => set('locationRoom', e.target.value)}
          />
          <TextInput
            label="Corredor"
            value={form.locationAisle}
            onChange={(e) => set('locationAisle', e.target.value)}
          />
          <TextInput
            label="Prateleira"
            value={form.locationShelf}
            onChange={(e) => set('locationShelf', e.target.value)}
          />
          <TextInput
            label="Posição"
            value={form.locationPosition}
            onChange={(e) => set('locationPosition', e.target.value)}
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Especificações técnicas</h3>
        <Textarea
          label={selectedCategoryGroup === 'furniture' ? 'Informações adicionais do mobiliário' : 'Especificações técnicas'}
          rows={4}
          value={form.specsText}
          onChange={(e) => set('specsText', e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-500">
          {selectedCategoryGroup === 'furniture'
            ? 'Use para registrar características que não estejam nos campos específicos.'
            : 'Texto livre. Aparece na aba "Detalhes" do ativo.'}
        </p>
        {selectedCategoryGroup === 'furniture' && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {FURNITURE_SPEC_FIELDS.map(({ key, label }) => (
              <TextInput
                key={key}
                label={label}
                value={form.furnitureSpecs[key]}
                onChange={(e) => setForm((current) => ({
                  ...current,
                  furnitureSpecs: { ...current.furnitureSpecs, [key]: e.target.value },
                }))}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Observações</h3>
        <Textarea
          label="Notas internas sobre o ativo"
          rows={3}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </section>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Salvando...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
